import { useActionHistory } from "@/hooks/useActionHistory";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { socketsAtom } from "@/store/socketsAtom";
import { UserAction } from "@/types/actionHistory";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useAtomValue } from "jotai";

interface RemovePatientTabParams {
  patientId: string;
  encounterId?: string;
}

interface RemovePatientTabResponse {
  patients: { addedAt: string; patientId: string }[];
}

export const useRemovePatientTab = (isTabSelected: boolean) => {
  const { navigate, history } = useRouter();
  const queryClient = useQueryClient();
  const chartingSocket = useAtomValue(socketsAtom)?.charting;
  const { trackAction: trackActionHistory } = useActionHistory();

  return useApiMutation<RemovePatientTabResponse, RemovePatientTabParams>({
    mutationOptions: {
      mutationKey: ["provider", "remove-patient"],
      mutationFn: async (params) => {
        const response = await axiosInstance.post(
          "/new-api/providers/remove-patient-tab",
          params,
        );
        return response.data;
      },
      onSuccess: (data, params) => {
        // Track the patient tab closure action
        trackActionHistory(UserAction.PATIENT_TAB_CLOSED, {
          patientId: params.patientId,
          encounterId: params.encounterId,
        });

        queryClient.invalidateQueries({
          queryKey: ["provider", "patient-tabs"],
        });

        if (chartingSocket) {
          chartingSocket.emit("leave_charting_for_patient", {
            patientId: params.patientId,
          });
        }

        if (data?.patients.length === 0) {
          navigate({
            to: "/live-queue",
          });
        } else if (isTabSelected) {
          history.go(-1);
        }
      },
    },
  });
};
